import { z } from 'zod';

export const RESPONSE_MODEL_IDS = [
  'gpt-5.6-luna',
  'gpt-6-luna',
  'claude-haiku-5-5',
] as const;
export const REASONING_EFFORTS = [
  'none',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
] as const;
export const responseModelIdSchema = z.enum(RESPONSE_MODEL_IDS);
export const reasoningEffortSchema = z.enum(REASONING_EFFORTS);
export const responseProcessingModeSchema = z.enum(['standard', 'fast']);
export type ResponseModelId = z.infer<typeof responseModelIdSchema>;
export type ReasoningEffort = z.infer<typeof reasoningEffortSchema>;
export type ResponseModelProvider = 'openai' | 'anthropic';

// What each provider documents for the model: Claude Haiku 5.5 has no
// reasoning-free effort level and no Fast processing.
export const RESPONSE_MODELS: ReadonlyArray<{
  model: ResponseModelId;
  displayName: string;
  provider: ResponseModelProvider;
  reasoningEfforts: readonly ReasoningEffort[];
  fastProcessing: boolean;
}> = [
  {
    model: 'gpt-5.6-luna',
    displayName: 'GPT-5.6 Luna',
    provider: 'openai',
    reasoningEfforts: REASONING_EFFORTS,
    fastProcessing: true,
  },
  {
    model: 'gpt-6-luna',
    displayName: 'GPT-6 Luna',
    provider: 'openai',
    reasoningEfforts: REASONING_EFFORTS,
    fastProcessing: true,
  },
  {
    model: 'claude-haiku-5-5',
    displayName: 'Claude Haiku 5.5',
    provider: 'anthropic',
    reasoningEfforts: ['low', 'medium', 'high', 'xhigh', 'max'],
    fastProcessing: false,
  },
];

export function responseModelEntry(model: ResponseModelId) {
  const entry = RESPONSE_MODELS.find((candidate) => candidate.model === model);
  if (!entry) throw new Error(`Response model ${model} has no catalog entry.`);
  return entry;
}

export const DEFAULT_RESPONSE_OUTPUT_TOKENS = 8_000;
export const responseOutputTokensSchema = z
  .number()
  .int()
  .min(1_024)
  .max(32_000);
export const responseModelSettingsSchema = z
  .object({
    model: responseModelIdSchema,
    reasoningEffort: reasoningEffortSchema,
    maxOutputTokens: responseOutputTokensSchema.optional(),
    processingMode: responseProcessingModeSchema.optional(),
  })
  .strict()
  .superRefine((settings, context) => {
    const entry = responseModelEntry(settings.model);
    if (!entry.reasoningEfforts.includes(settings.reasoningEffort))
      context.addIssue({
        code: 'custom',
        path: ['reasoningEffort'],
        message: `${entry.displayName} does not support reasoning effort ${settings.reasoningEffort}.`,
      });
    if (settings.processingMode === 'fast' && !entry.fastProcessing)
      context.addIssue({
        code: 'custom',
        path: ['processingMode'],
        message: `${entry.displayName} has no Fast processing.`,
      });
  });
export type ResponseModelSettings = z.infer<typeof responseModelSettingsSchema>;

// Immutable releases without this field keep the former model and provider-default effort.
export function resolveResponseModel(
  settings?: ResponseModelSettings
): ResponseModelSettings {
  return responseModelSettingsSchema.parse(
    settings ?? {
      model: 'gpt-5.6-luna',
      reasoningEffort: 'medium',
    }
  );
}

export const availableResponseModelsSchema = z
  .object({
    models: z
      .array(
        z
          .object({
            model: responseModelIdSchema,
            displayName: z.string(),
            available: z.boolean(),
          })
          .strict()
      )
      .max(RESPONSE_MODEL_IDS.length),
  })
  .strict();
